/*
 * CalcEngine — pure expression evaluator for the /calculator tool.
 *
 * Arithmetic is DECIMAL, not binary floating point: every number is a
 * decimal.js value carried at PRECISION significant digits (50), and the
 * screen shows at most SHOW_SIG (16). That gap is the whole design:
 *
 *   • What the person types is exact. 0.1 + 0.2 − 0.3 is 0, not 5.55e-17;
 *     1.15 × 100 is 115, not 114.99999999999999.
 *   • A result is never cut down to what the screen shows. 1 ÷ 3 = × 3 = is
 *     1, not 0.999999999999 — the next step starts from all 50 digits
 *     (the page passes the previous result back as a slot, `@n`).
 *   • Irrational results (π, √2, sin 1) carry 34 digits past the screen,
 *     so every digit shown is the correctly rounded true digit.
 *   • A value whose exact decimal does not fit the screen is shown rounded
 *     AND marked approximate (`approx: true` → the page prints «≈»). π is
 *     «≈ 3,141592653589793»: the reader sees that the last digit is a
 *     rounding, not a claim about the 16th digit of π.
 *
 * Zero is snapped only below the arithmetic's own noise floor — a result
 * smaller than the rounding error its inputs already carry (relative
 * 10^-(PRECISION-10) of the operands for ±, of the argument for sin/cos).
 * That is why sin(180°) is 0 and tan(90°) is "undefined" while sin(1e-20)
 * stays 1e-20. Measured against mpmath — docs/why/calculator-precision.md.
 *
 *   CalcEngine.evaluate(expr, {angle:'deg'|'rad', slots:[Decimal]})
 *       -> {ok:true, value:Decimal} | {ok:false, error}
 *   CalcEngine.present(value, {locale}) -> {text, approx, plain, speak}
 *   CalcEngine.format(value, {locale})  -> String ("≈ " prefix when approx)
 *   CalcEngine.serialize(value) / revive(string)   (tape + memory storage)
 *   CalcEngine.tokenize(expr) / parse(tokens)      (exported for unit tests)
 *
 * Grammar (recursive descent — unary-minus precedence is exactly the math
 * convention: -2^2 = -4, 2^-3 = 0.125):
 *   expr    := term (('+'|'-') term)*
 *   term    := unary (('*'|'/') unary | <implicit-*> unary)*
 *   unary   := ('-'|'+') unary | power
 *   power   := postfix ('^' unary)?            // right-assoc, RHS is unary
 *   postfix := primary ('!')*
 *   primary := NUMBER | CONST | SLOT | FUNC '(' expr ')' | '(' expr ')'
 *
 * Error codes: empty, syntax, paren_mismatch, div_zero, domain, overflow.
 */
(function (global) {
    'use strict';

    var PRECISION = 50;     // digits carried through every operation
    var CLEAN_SIG = 34;     // digits trusted when deciding "is this exact?"
    var SHOW_SIG = 16;      // digits on the screen
    var INT_FULL = 20;      // an exact integer up to this many digits is shown whole
    var NOISE_EXP = PRECISION - 10;   // relative noise floor: 1e-40

  function build(Base) {
    var D = Base.clone({
        precision: PRECISION,
        rounding: Base.ROUND_HALF_EVEN,
        toExpNeg: -40, toExpPos: 60,
        maxE: 9999, minE: -9999          // |x| ≥ 1e10000 → overflow (as Windows Calculator)
    });
    var ZERO = new D(0), ONE = new D(1);
    var PI = D.acos(-1);
    var E = D.exp(1);
    var NOISE = new D('1e-' + NOISE_EXP);

    var ANGLE = 'rad';

    // ── noise floor ────────────────────────────────────────────────────
    // A result below the error its inputs already carry is zero. `scale` is
    // the size of those inputs: the larger operand of ±, the argument of
    // sin/cos (its error moves sin by the same absolute amount).
    function snap(r, scale) {
        if (r.isZero()) return r;
        return r.abs().lt(scale.abs().times(NOISE)) ? ZERO : r;
    }
    function checked(r) {
        if (r.isNaN()) throw 'domain';
        if (!r.isFinite()) throw 'overflow';
        return r;
    }

    function toRad(x) {
        if (ANGLE !== 'deg') return x;
        // reduce the exact degree value first: sin(36000000000000000180°) is sin(180°)
        return x.mod(360).times(PI).div(180);
    }
    function fromRad(x) { return ANGLE === 'deg' ? x.times(180).div(PI) : x; }
    function clampUnit(x) {
        // asin(sin(90°)) must not fail on a value one noise-step above 1
        if (x.abs().gt(ONE)) {
            if (x.abs().minus(ONE).lt(NOISE)) return x.isNeg() ? ONE.neg() : ONE;
            throw 'domain';
        }
        return x;
    }
    // decimal.js reduces a radian argument with π carried to (precision +
    // exponent) digits and keeps π to 1025: past ~1e968 it throws mid-call
    // (see restore()). Refuse long before that.
    var TRIG_MAX = new D('1e900');
    function trigArg(r) { if (r.abs().gte(TRIG_MAX)) throw 'overflow'; return r; }
    function sinR(r) { return snap(D.sin(trigArg(r)), r); }
    function cosR(r) { return snap(D.cos(trigArg(r)), r); }

    var FUNCS = {
        sin: function (x) { return sinR(toRad(x)); },
        cos: function (x) { return cosR(toRad(x)); },
        tan: function (x) {
            var r = toRad(x), c = cosR(r);
            if (c.isZero()) throw 'domain';            // tan(90°): undefined
            return sinR(r).div(c);
        },
        asin: function (x) { return fromRad(D.asin(clampUnit(x))); },
        acos: function (x) { return fromRad(D.acos(clampUnit(x))); },
        atan: function (x) { return fromRad(D.atan(x)); },
        sqrt: function (x) { if (x.isNeg() && !x.isZero()) throw 'domain'; return x.sqrt(); },
        cbrt: function (x) { return x.cbrt(); },        // real root: ∛(−8) = −2
        ln: function (x) { if (!x.gt(ZERO)) throw 'domain'; return x.ln(); },
        log: function (x) { if (!x.gt(ZERO)) throw 'domain'; return x.log(10); }
    };

    // ── Tokenizer ──────────────────────────────────────────────────────
    // Token shapes: {t:'num',v} {t:'const',v} {t:'func',v:name}
    //               {t:'op',v:'+|-|*|/|^'} {t:'lp'} {t:'rp'} {t:'fact'} {t:'pct'}
    // `@n` is slot n of opts.slots — a full-precision earlier result.
    function tokenize(expr, slots) {
        var s = String(expr == null ? '' : expr)
            .replace(/×/g, '*')
            .replace(/÷/g, '/')
            .replace(/[−–—]/g, '-')
            .replace(/π/gi, 'pi')
            .replace(/∛/g, 'cbrt')
            .replace(/√/g, 'sqrt')
            .replace(/[٫،]/g, '.')
            .replace(/,/g, '.');

        var tokens = [];
        var i = 0, n = s.length;
        while (i < n) {
            var c = s[i];
            if (c === ' ' || c === '\t' || c === '\n') { i++; continue; }
            if ((c >= '0' && c <= '9') || c === '.') {
                var mn = /^(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?/i.exec(s.slice(i));
                if (!mn) throw 'syntax';
                var num = new D(mn[0]);
                if (!num.isFinite()) throw 'overflow';
                tokens.push({ t: 'num', v: num });
                i += mn[0].length;
                continue;
            }
            if (c === '@') {
                var ms = /^@(\d+)/.exec(s.slice(i));
                if (!ms || !slots || !slots[+ms[1]]) throw 'syntax';
                tokens.push({ t: 'const', v: slots[+ms[1]] });
                i += ms[0].length;
                continue;
            }
            if (/[a-z]/i.test(c)) {
                var ml = /^[a-z]+/i.exec(s.slice(i));
                var name = ml[0].toLowerCase();
                if (name === 'pi') tokens.push({ t: 'const', v: PI });
                else if (name === 'e') tokens.push({ t: 'const', v: E });
                else if (FUNCS[name]) tokens.push({ t: 'func', v: name });
                else throw 'syntax';
                i += ml[0].length;
                continue;
            }
            if (c === '+' || c === '-' || c === '*' || c === '/' || c === '^') {
                tokens.push({ t: 'op', v: c }); i++; continue;
            }
            if (c === '(') { tokens.push({ t: 'lp' }); i++; continue; }
            if (c === ')') { tokens.push({ t: 'rp' }); i++; continue; }
            if (c === '!') { tokens.push({ t: 'fact' }); i++; continue; }
            if (c === '%') { tokens.push({ t: 'pct' }); i++; continue; }
            throw 'syntax';
        }
        return tokens;
    }

    // ── Contextual percent rewrite ─────────────────────────────────────
    // Pocket-calculator semantics for a single A±B% / A*B% / A/B% / bare B%
    // step. Operates on the token list before parsing. Chained percents
    // beyond one step degrade to plain /100.
    function primaryStart(tokens, j) {
        if (j < 0) return -1;
        var tk = tokens[j];
        if (!tk) return -1;
        if (tk.t === 'num' || tk.t === 'const') return j;
        if (tk.t === 'fact') return primaryStart(tokens, j - 1);
        if (tk.t === 'rp') {
            var depth = 0;
            for (var k = j; k >= 0; k--) {
                if (tokens[k].t === 'rp') depth++;
                else if (tokens[k].t === 'lp') {
                    depth--;
                    if (depth === 0) {
                        if (k > 0 && tokens[k - 1].t === 'func') return k - 1;
                        return k;
                    }
                }
            }
            return -1;
        }
        return -1;
    }

    function rewritePercent(tokens) {
        var HUNDRED = { t: 'num', v: new D(100) };
        for (var guard = 0; guard < 200; guard++) {
            var idx = -1;
            for (var k = 0; k < tokens.length; k++) {
                if (tokens[k].t === 'pct') { idx = k; break; }
            }
            if (idx < 0) break;

            var ps = primaryStart(tokens, idx - 1);
            if (ps < 0) throw 'syntax';
            var primary = tokens.slice(ps, idx);
            var prev = ps - 1;
            var pt = prev >= 0 ? tokens[prev] : null;
            var repl;

            if (pt && pt.t === 'op' && (pt.v === '+' || pt.v === '-')) {
                // A ± B%  →  A ± (A * B / 100)
                var as = primaryStart(tokens, prev - 1);
                if (as < 0) throw 'syntax';
                var A = tokens.slice(as, prev);
                repl = [{ t: 'lp' }]
                    .concat(A, [{ t: 'op', v: '*' }], primary,
                        [{ t: 'op', v: '/' }, HUNDRED, { t: 'rp' }]);
            } else {
                // bare B%, or *B% / /B% / ^B%  →  (B / 100)
                repl = [{ t: 'lp' }]
                    .concat(primary, [{ t: 'op', v: '/' }, HUNDRED, { t: 'rp' }]);
            }
            Array.prototype.splice.apply(tokens, [ps, idx - ps + 1].concat(repl));
        }
        return tokens;
    }

    // ── Operations ─────────────────────────────────────────────────────
    function opDiv(a, b) {
        if (b.isZero()) { if (a.isZero()) throw 'domain'; throw 'div_zero'; }
        return checked(a.div(b));
    }
    function factorial(x) {
        if (!x.isInteger() || x.isNeg()) throw 'domain';
        if (x.gt(3300)) throw 'overflow';                 // 3249! ≥ 1e10000
        var r = ONE;
        for (var k = 2, n = x.toNumber(); k <= n; k++) {
            r = r.times(k);
            if (!r.isFinite()) throw 'overflow';
        }
        return r;
    }
    // An exponent that is p/q with odd q gives a real root of a negative
    // base: (-8)^(1/3) = -2. The exponent itself may be 1/3 to 50 digits.
    function oddRootExponent(y) {
        var x = y.abs(), h0 = ZERO, h1 = ONE, k0 = ONE, k1 = ZERO;
        for (var i = 0; i < 20; i++) {
            var a = x.floor();
            var h2 = a.times(h1).plus(h0), k2 = a.times(k1).plus(k0);
            h0 = h1; h1 = h2; k0 = k1; k1 = k2;
            if (k1.gt(1000)) return null;
            if (h1.div(k1).minus(y.abs()).abs().lt(NOISE)) {
                return k1.mod(2).eq(1) ? { p: h1, q: k1 } : null;
            }
            var f = x.minus(a);
            if (f.isZero()) return null;
            x = ONE.div(f);
        }
        return null;
    }
    function opPow(b, y) {
        if (b.isZero()) {
            if (y.isNeg()) throw 'div_zero';
            return y.isZero() ? ONE : ZERO;
        }
        if (b.isNeg() && !y.isInteger()) {
            var pq = oddRootExponent(y);
            if (!pq) throw 'domain';
            var mag = b.abs().pow(y);
            return checked(pq.p.mod(2).eq(1) ? mag.neg() : mag);
        }
        return checked(b.pow(y));
    }

    // ── Recursive-descent parser (evaluates inline) ────────────────────
    function parse(tokens) {
        if (!tokens || tokens.length === 0) throw 'empty';
        var pos = 0;

        function peek() { return tokens[pos]; }
        function isPrimaryStart(tk) {
            return !!tk && (tk.t === 'num' || tk.t === 'const' || tk.t === 'lp' || tk.t === 'func');
        }

        function parseExpr() {
            var v = parseTerm();
            for (var tk = peek(); tk && tk.t === 'op' && (tk.v === '+' || tk.v === '-'); tk = peek()) {
                pos++;
                var rhs = parseTerm();
                var r = tk.v === '+' ? v.plus(rhs) : v.minus(rhs);
                // cancellation below the operands' own noise is zero: 0.1+0.2−0.3
                v = checked(snap(r, D.max(v.abs(), rhs.abs())));
            }
            return v;
        }
        function parseTerm() {
            var v = parseUnary();
            for (;;) {
                var tk = peek();
                if (tk && tk.t === 'op' && (tk.v === '*' || tk.v === '/')) {
                    pos++;
                    var rhs = parseUnary();
                    v = tk.v === '*' ? checked(v.times(rhs)) : opDiv(v, rhs);
                } else if (isPrimaryStart(tk)) {
                    v = checked(v.times(parseUnary()));   // implicit: 2pi, 2(3)
                } else {
                    break;
                }
            }
            return v;
        }
        function parseUnary() {
            var tk = peek();
            if (tk && tk.t === 'op' && (tk.v === '-' || tk.v === '+')) {
                pos++;
                var v = parseUnary();
                return tk.v === '-' ? v.neg() : v;
            }
            return parsePower();
        }
        function parsePower() {
            var base = parsePostfix();
            var tk = peek();
            if (tk && tk.t === 'op' && tk.v === '^') {
                pos++;
                return opPow(base, parseUnary());
            }
            return base;
        }
        function parsePostfix() {
            var v = parsePrimary();
            while (peek() && peek().t === 'fact') { pos++; v = factorial(v); }
            return v;
        }
        function parsePrimary() {
            var tk = peek();
            if (!tk) throw 'syntax';
            if (tk.t === 'num' || tk.t === 'const') { pos++; return tk.v; }
            if (tk.t === 'func') {
                pos++;
                if (!peek() || peek().t !== 'lp') throw 'syntax';
                pos++;
                var arg = parseExpr();
                if (!peek() || peek().t !== 'rp') throw 'paren_mismatch';
                pos++;
                return checked(FUNCS[tk.v](arg));
            }
            if (tk.t === 'lp') {
                pos++;
                var v = parseExpr();
                if (!peek() || peek().t !== 'rp') throw 'paren_mismatch';
                pos++;
                return v;
            }
            if (tk.t === 'rp') throw 'paren_mismatch';
            throw 'syntax';
        }

        var result = parseExpr();
        if (pos < tokens.length) {
            throw tokens[pos].t === 'rp' ? 'paren_mismatch' : 'syntax';
        }
        return result;
    }

    var KNOWN_ERRORS = ['empty', 'syntax', 'paren_mismatch', 'div_zero', 'domain', 'overflow'];

    function evaluate(expr, opts) {
        opts = opts || {};
        ANGLE = opts.angle === 'deg' ? 'deg' : 'rad';
        try {
            var tokens = tokenize(expr, opts.slots);
            if (tokens.length === 0) return { ok: false, error: 'empty' };
            rewritePercent(tokens);
            var v = parse(tokens);
            if (v.isNaN()) return { ok: false, error: 'domain' };
            if (!v.isFinite()) return { ok: false, error: 'overflow' };
            if (v.isZero()) v = ZERO;                     // normalise -0
            return { ok: true, value: v };
        } catch (err) {
            if (typeof err !== 'string') restore();
            var code = (typeof err === 'string') ? err : 'overflow';
            if (KNOWN_ERRORS.indexOf(code) < 0) code = 'syntax';
            return { ok: false, error: code };
        }
    }

    // A decimal.js exception thrown mid-operation leaves the library with its
    // internal rounding switched off and the raised working precision in
    // place (getPi does not reset them; getLn10 does) — every later answer is
    // then computed unbounded: wrong, or a frozen tab. Put both back.
    function restore() {
        D.set({ precision: PRECISION, rounding: Base.ROUND_HALF_EVEN });
        D.sum(0);           // sum() ends with `external = true`
    }

    // ── Presentation ───────────────────────────────────────────────────
    var SEP_CACHE = {};
    function separators(locale) {
        if (SEP_CACHE[locale]) return SEP_CACHE[locale];
        var dec = '.', grp = ',';
        try {
            new Intl.NumberFormat(locale).formatToParts(1234567.5).forEach(function (p) {
                if (p.type === 'decimal') dec = p.value;
                if (p.type === 'group') grp = p.value;
            });
        } catch (_) {}
        return (SEP_CACHE[locale] = { dec: dec, grp: grp });
    }
    function group(intDigits, grp) {
        return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, grp);
    }
    var SUP = { '-': '⁻', '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹' };

    /**
     * What the screen shows for a value.
     *   text   — for the display: grouped, locale decimal sign, «× 10ⁿ» form
     *   approx — the exact value has more digits than shown (page prints «≈»)
     *   plain  — for the clipboard: no grouping, locale decimal sign, «E» exponent
     *   speak  — for speech: the shown digits with a "." decimal point
     */
    function present(value, opts) {
        opts = opts || {};
        var sep = separators(opts.locale || 'en');
        var v = value instanceof D ? value : new D(value);
        if (v.isZero()) return { text: '0', approx: false, plain: '0', speak: '0' };

        var clean = v.toSignificantDigits(CLEAN_SIG, D.ROUND_HALF_EVEN);
        var isInt = clean.isInteger();
        var exact = clean.sd() <= SHOW_SIG || (isInt && clean.abs().lt('1e' + INT_FULL));
        var shown = exact ? clean : clean.toSignificantDigits(SHOW_SIG, D.ROUND_HALF_UP);

        var abs = shown.abs();
        var neg = shown.isNeg() ? '−' : '';
        var lead = abs.lt(1) ? -abs.e - 1 : 0;           // zeros after "0," before the first digit
        // Positional only when every printed digit is a real one: an exact
        // integer up to INT_FULL digits, or a value whose 16 shown digits
        // reach its units place (a rounded 1.2e19 would print invented zeros).
        var positional = (exact && shown.isInteger() && abs.lt('1e' + INT_FULL))
            || (abs.lt('1e' + SHOW_SIG) && abs.gte('1e-9') && lead + shown.sd() <= 20);

        if (positional) {
            var fixed = abs.toFixed();                    // plain decimal digits, no exponent
            var parts = fixed.split('.');
            var txt = group(parts[0], sep.grp) + (parts[1] ? sep.dec + parts[1] : '');
            return {
                text: neg + txt,
                approx: !exact,
                plain: (neg ? '-' : '') + parts[0] + (parts[1] ? sep.dec + parts[1] : ''),
                speak: (neg ? '-' : '') + fixed
            };
        }
        var exp = abs.e;
        var mant = abs.div(new D('1e' + exp)).toFixed();  // 1 ≤ mant < 10, exact digits
        var m = mant.split('.');
        var mtxt = m[0] + (m[1] ? sep.dec + m[1] : '');
        var etxt = String(exp).split('').map(function (ch) { return SUP[ch]; }).join('');
        return {
            text: neg + mtxt + ' × 10' + etxt,
            approx: !exact,
            plain: (neg ? '-' : '') + mtxt + 'E' + exp,
            speak: (neg ? '-' : '') + mant + 'e' + exp
        };
    }

    function format(value, opts) {
        var p = present(value, opts);
        return (p.approx ? '≈ ' : '') + p.text;
    }

    // Storage form for tape and memory: every digit we carry, "." decimal.
    function serialize(value) { return (value instanceof D ? value : new D(value)).toString(); }
    function revive(str) {
        try {
            var v = new D(String(str));
            return v.isFinite() ? v : null;
        } catch (_) { return null; }
    }

    return {
        evaluate: evaluate,
        present: present,
        format: format,
        separators: separators,
        serialize: serialize,
        revive: revive,
        tokenize: tokenize,
        parse: parse,
        Decimal: D,
        ZERO: ZERO,
        SHOW_SIG: SHOW_SIG
    };
  }

    function publish(Base) {
        var API = build(Base);
        if (typeof module !== 'undefined' && module.exports) module.exports = API;
        global.CalcEngine = API;
        try { global.dispatchEvent(new Event('calc-engine:ready')); } catch (_) {}
    }

    // decimal.js is the tag right before this one. When the network drops it,
    // the layout re-inserts it in place (parser-blocking) and it is here now.
    // When a proxy served HTML in its place, the retry is async — wait for it
    // instead of leaving the page without an engine.
    if (global.Decimal) {
        publish(global.Decimal);
    } else {
        var waited = 0;
        var iv = setInterval(function () {
            waited += 250;
            if (global.Decimal) { clearInterval(iv); publish(global.Decimal); }
            else if (waited >= 30000) clearInterval(iv);
        }, 250);
    }

})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
